import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import { getPropClassesFromInstance } from 'lib/getPropClasses';
import Box from 'components/Box';
import Button from 'components/ButtonDeprecated';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import TitleHeader from 'components/TitleHeader';
import { GlyphName } from '@remind/r101-glyphs';

import './styles/DialogContent.scss';

/**
 * ```example
 * <DialogContent
 *   subtext="Subtext"
 *   title="Title">
 *   <BoxBody>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </DialogContent>
 * ```
 *
 * ```example
 * <DialogContent
 *   glyph="remind-icon"
 *   glyphColor="mascot"
 *   size="xsmall"
 *   subtext="Subtext"
 *   title="Title">
 *   <BoxBody>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </DialogContent>
 * ```
 *
 * ```example
 * <DialogContent
 *   image="/assets/images/apple.svg"
 *   imageSize="xxxsmall"
 *   size="xsmall"
 *   subtext="Subtext"
 *   title="Title">
 *   <BoxBody>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </DialogContent>
 * ```
 *
 * ```example
 * <DialogContent
 *   headerImage="/assets/images/phoneCalls/PhoneCallModal/click-to-call-answer.svg"
 *   size="xsmall"
 *   subtext="Subtext"
 *   title="Title">
 *   <BoxBody>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </DialogContent>
 * ```
 *
 *
 * # TODO
 * * Reconcile with [Box](/internal/components/Box) components
 */

type GlyphColors = 'inherit' | 'apple' | 'atlas' | 'pennant';
type HeaderTextAlign = 'left' | 'center' | 'right';
type ImageSizes = 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall' | 'xxxsmall';
type Sizes = 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall';

type Props = {
  className?: string;
  glyph?: GlyphName;
  glyphColor?: GlyphColors;
  headerTextAlign?: HeaderTextAlign;
  headerImage?: string;
  hideCloseButton?: boolean;
  hideOnBlur?: boolean;
  hidePreviousButton?: boolean;
  image?: string;
  imageSize?: ImageSizes;
  size?: Sizes;
  subtext?: React.ReactNode;
  title?: React.ReactNode;
  onDialogDismiss?: () => void;
  onRequestHide?: () => void;
  onRequestPrevious?: () => void;
  borderlessContent?: boolean;
};

type State = {
  paddingTop: number | undefined;
};

class DialogContent extends React.Component<Props, State> {
  timeout: number | undefined = undefined;

  static propClasses = {
    size: {
      xlarge: 'DialogContent--xlarge',
      large: 'DialogContent--large',
      medium: 'DialogContent--medium',
      small: 'DialogContent--small',
      xsmall: 'DialogContent--xsmall',
    },
  };

  static defaultProps: Props = {
    headerTextAlign: 'center',
    hideCloseButton: true,
    hidePreviousButton: true,
    size: 'medium',
    onRequestPrevious: () => {},
  };

  state: State = {
    paddingTop: undefined,
  };

  componentDidMount() {
    window.addEventListener('resize', this.resetPosition, false);
    // If the DialogContent has a headerImage, this allows DialogContent to wait until
    // the headerImage is loaded before trying to center the dialog
    if (!this.props.headerImage) {
      // FIXME: This is to allow ScrollableArea to set a height before we try to center the dialog
      this.timeout = window.setTimeout(() => this.resetPosition(), 0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resetPosition, false);
    window.clearTimeout(this.timeout);
  }

  closeDialog = (event: React.MouseEvent<HTMLElement>) => {
    const box = ReactDOM.findDOMNode(this.refs.box);
    // in IE only, box is somehow sometimes null
    // https://my.trackjs.com/messages/filtered?hash=c677c309de472679
    if (box && box.contains(event.target as HTMLElement)) {
      return;
    }

    event.stopPropagation();
    if (this.props.onDialogDismiss) {
      this.props.onDialogDismiss();
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const box = ReactDOM.findDOMNode(this.refs.box);
    if (!this.props.hideOnBlur || (box && box.contains(event.target as HTMLElement))) {
      return;
    }

    if (this.props.onDialogDismiss) {
      this.props.onDialogDismiss();
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  resetPosition = () => {
    const root = ReactDOM.findDOMNode(this) as HTMLElement;
    if (!root) {
      return;
    }

    const container = ReactDOM.findDOMNode(this.refs.container) as HTMLElement;
    if (!container) {
      return;
    }

    const paddingTop = (root.offsetHeight - container.offsetHeight) / 2;

    this.setState({ paddingTop });
  };

  renderPreviousButton = () => {
    const { hidePreviousButton, onRequestPrevious } = this.props;

    if (hidePreviousButton) {
      return null;
    }

    return (
      <Button
        aria-label="Back"
        color="mascot"
        level="icon"
        className="DialogContent-previousButton"
        eventElementName="DialogContent-previousButton"
        onClick={onRequestPrevious}>
        <Glyph name="arrow-left" size="large" solo />
      </Button>
    );
  };

  renderHeader = () => {
    const { glyph, glyphColor, headerTextAlign, image, imageSize, subtext, title } = this.props;
    const previousButton = this.renderPreviousButton();

    if (!(previousButton || glyph || image || title || subtext)) {
      return null;
    }

    return (
      <header className="DialogContent-header">
        {previousButton}
        <TitleHeader
          glyph={glyph}
          glyphColor={glyphColor}
          image={image}
          imageSize={imageSize}
          title={title}
          subtext={subtext}
          textAlign={headerTextAlign}
          flush
        />
      </header>
    );
  };

  render() {
    const { children, className, headerImage, hideCloseButton, borderlessContent } = this.props;
    const { paddingTop } = this.state;

    return (
      <div
        style={paddingTop ? {} : { opacity: 0.01 }}
        className={cx('DialogContent', getPropClassesFromInstance(this), className)}>
        <div style={{ paddingTop }} className="DialogContent-wrap" onClick={this.handleClick}>
          <div ref="container" className="DialogContent-container">
            {borderlessContent ? (
              children
            ) : (
              <Box ref="box" className="DialogContent-box">
                {headerImage && (
                  <Image
                    className="DialogContent-headerImage"
                    src={headerImage}
                    onLoad={this.resetPosition}
                    full
                  />
                )}
                {this.renderHeader()}
                {children}
              </Box>
            )}
            {!hideCloseButton && (
              <Button
                aria-label="Close"
                color="whiteout"
                level="icon"
                className="DialogContent-closeButton"
                eventElementName="DialogContent-closeButton"
                onClick={this.closeDialog}>
                <Glyph name="close" size="large" solo />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default DialogContent;
